import dayjs from 'dayjs';

import type { BannerRootData } from '../schemas/bannerSchema';
import {
  BannerDestinationType,
  BannerStatusEnum,
} from '../types/bannerManagement';

export type BannerSubmitIntent = 'draft' | 'publish';

export interface BannerPayload {
  title: string;
  effectiveDate?: string;
  expirationDate?: string | null;
  deviceType: string;
  slotType: string;
  targetMarket: string;
  order: number;
  link?: string | null;
  productId: number;
  categoryId: number;
  status?: BannerStatusEnum;
}

const DEFAULT_BANNER_LINK = 'https://www.queimadiaria.com';

const parseNumericId = (value?: string): number => {
  if (!value) return 0;

  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const toIsoString = (value: unknown): string | null => {
  if (!value) return null;

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value.toISOString() : null;
  }

  if (value instanceof Date) {
    return !isNaN(value.getTime()) ? value.toISOString() : null;
  }

  const parsed = dayjs(value as string);
  return parsed.isValid() ? parsed.toISOString() : null;
};

export function buildBannerPayload(data: BannerRootData): BannerPayload {
  const effectiveDate = toIsoString(data.schedule?.scheduledDate);

  const expirationDate = data.schedule?.withoutEndDate
    ? null
    : toIsoString(data.schedule?.scheduledTime);

  const destinationLink = data.content.destinationLink?.trim();

  return {
    title: data.content.name,
    ...(effectiveDate && { effectiveDate }),
    ...(expirationDate && { expirationDate }),
    deviceType: String(data.configuration.platform),
    slotType: String(data.configuration.slotType),
    targetMarket: String(data.configuration.segment),
    order: 0,
    link:
      data.content.destinationType === BannerDestinationType.Link
        ? destinationLink || DEFAULT_BANNER_LINK
        : null,
    productId:
      data.content.destinationType === BannerDestinationType.Product
        ? parseNumericId(data.content.destinationProduct)
        : 0,
    categoryId:
      data.content.destinationType === BannerDestinationType.Category
        ? parseNumericId(data.content.destinationCategory)
        : 0,
    ...(data.schedule?.isActive === false && {
      status: BannerStatusEnum.Inactive,
    }),
    ...(data.schedule?.isActive === true && {
      status: BannerStatusEnum.Active,
    }),
  };
}
